import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelM } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { fantasy } from '@sorare/core/src/lib/glossary';

import type {
  hallOfFameBannerQuery,
  hallOfFameBannerQueryVariables,
} from './__generated__/index.graphql';
import background from './assets/background.jpg';

const Root = styled.div`
  padding: 0 var(--triple-unit);
  display: grid;
  grid-template-columns: 1fr max-content 1fr;
  grid-template-areas: 'left center right';
  background-image: url(${background});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  gap: var(--quadruple-unit);
  overflow: hidden;

  &.rounded {
    border-radius: var(--double-unit);
  }
`;
const PlayersWrapper = styled(Horizontal)`
  grid-area: left;
  gap: 0;
  justify-self: center;
  position: relative;
  width: 100%;
  align-items: flex-end;
  container-type: inline-size;
  transform: translateY(var(--unit));
`;
const PlayerImg = styled.img`
  min-width: calc(8 * var(--unit));
  width: 20%;
  max-width: calc(12 * var(--unit));
  object-fit: contain;
  display: none;
  transform: translateX(calc(-1 * var(--index) * 30%));
  @container (width > 220px) {
    display: flex;
  }
`;
const TitleWrapper = styled(Vertical).attrs({ gap: 2, center: true })`
  grid-area: center;
  padding: var(--double-unit) 0;

  &.tall {
    padding-top: var(--quadruple-unit);
  }
`;
const IconWrapper = styled.div`
  grid-area: right;
  align-self: center;
  justify-self: flex-end;
`;

const HALL_OF_FAME_BANNER_QUERY = gql`
  query hallOfFameBannerQuery(
    $sport: Sport!
    $rarity: Rarity!
    $seasonStartYear: Int!
  ) {
    currentUser {
      slug
      hallOfFameSo5LeaderboardContenders(
        rarity: $rarity
        seasonStartYear: $seasonStartYear
        sport: $sport
      ) {
        slug
        so5Lineup {
          id
          so5Appearances {
            id
            anyPlayer {
              slug
              avatarPictureUrl
              fullPictureUrl
              squaredPictureUrl
            }
          }
        }
      }
    }
  }
` as TypedDocumentNode<hallOfFameBannerQuery, hallOfFameBannerQueryVariables>;

type Props = {
  seasonName: string;
  rounded?: boolean;
  tall?: boolean;
  to?: string;
  showPlayers?: boolean;
  rarity?: Rarity;
  seasonStartYear?: number;
  children?: ReactNode;
};
export const HallOfFameBanner = ({
  seasonName,
  rounded,
  tall,
  to,
  showPlayers = true,
  rarity,
  seasonStartYear,
  children,
}: Props) => {
  const { sport } = useSportContext();
  const { data } = useQuery(HALL_OF_FAME_BANNER_QUERY, {
    variables: {
      sport,
      rarity: rarity || Rarity.limited,
      seasonStartYear: seasonStartYear || 2024,
    },
    skip: !showPlayers || !rarity || !seasonStartYear,
  });
  const players = data?.currentUser?.hallOfFameSo5LeaderboardContenders
    ?.at(0)
    ?.so5Lineup?.so5Appearances?.map(appearance => appearance.anyPlayer);

  return (
    <ConditionalWrapper wrap={!!to} Wrapper={Link} props={{ to: to! }}>
      <Root className={classNames({ rounded })}>
        <PlayersWrapper>
          {players?.map((player, index) => (
            <PlayerImg
              key={player.slug}
              style={{ '--index': index } as CSSProperties}
              src={player.squaredPictureUrl}
              alt=""
            />
          ))}
        </PlayersWrapper>
        <TitleWrapper className={classNames('text-center', { tall })}>
          <Vertical center gap={0}>
            <BrandM>
              <FormattedMessage {...fantasy.hallOfFame} />
            </BrandM>
            <LabelM>
              <FormattedMessage
                id="HallOfFameBanner.subtitle"
                defaultMessage="Your {seasonName} Best Lineups"
                values={{ seasonName }}
              />
            </LabelM>
          </Vertical>
          {children}
        </TitleWrapper>
        {!!to && (
          <IconWrapper>
            <ChevronRightBold />
          </IconWrapper>
        )}
      </Root>
    </ConditionalWrapper>
  );
};
